import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";

const API = process.env.REACT_APP_BACKEND_URL;
const ACCESSORIES_HERO = "/products/xseries/731077_587141b453fa4b6381a1e3ef459cea20~mv2.jpg";
const fmt = (n) => n.toLocaleString("en-NZ", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const priceLabel = (m) => `${m.priceFrom ? "FROM " : ""}$${fmt(m.price)}`;

// Already native elsewhere on the site (Wellside Accessories / Drawers, Kitchens & Tables pages) —
// cross-linked here, not duplicated
const PROMOTED_PRODUCTS = [
  { name: "Bushtech Canvas Storage Pouches", price: 165.0, tagline: "See-Through Zip Pouches For Canopy Cupboards", thumb: "/products/camping-accessories/731077_accbfb95d943450898c8a24811114850~mv2.png", to: "/wellside-accessories/bushtech-canvas-storage-pouches" },
  { name: "Quick Pitch Complete Mini Kitchen", price: 3499.0, tagline: "Drawer, Prep Table & Fridge Slide — All In One", thumb: "/products/drawers/731077_2ad13fa5abbd472fb9cebf62091c53db~mv2.jpg", to: "/drawers-kitchens-tables/quick-pitch-complete-mini-kitchen" },
  { name: "Bushtech Cargo Net", price: 235.0, tagline: "Overhead Storage For Canopies & 4WD Wagons", thumb: "/products/drawers/731077_8e88178d3e7e4a92b4349e6234fd08a8~mv2.jpg", to: "/wellside-accessories/bushtech-cargo-net" },
];

const Card = ({ m, testid }) => (
  <Link
    to={m.to || `/camping-accessories/${m.slug}`}
    data-testid={testid}
    className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors bg-[var(--bg-secondary)]"
  >
    <div className="aspect-[4/3] overflow-hidden relative">
      <HoverImage primary={m.thumb} secondary={m.gallery?.[1]?.src} alt={m.name} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
      {m.badge && (
        <div className="absolute top-4 left-4">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">{m.badge}</span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-display text-lg md:text-xl text-white leading-tight tracking-wide">{m.name.toUpperCase()}</h3>
        <p className="text-[11px] font-mono uppercase tracking-wider text-white/70 mt-2 line-clamp-1">{m.tagline}</p>
        <div className="flex items-end justify-between gap-3 mt-4">
          <p className="font-display text-xl text-[var(--brand-primary)] leading-none">{priceLabel(m)}</p>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-white group-hover:text-[var(--brand-primary)] transition-colors">
            View <ArrowUpRight size={12} />
          </span>
        </div>
      </div>
    </div>
  </Link>
);

export default function CampingAccessoriesPage() {
  const [products, setProducts] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/campingaccessory`).then((res) => setProducts(res.data)).catch((err) => { console.error(err); setProducts([]); });
  }, []);

  if (products === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }

  return (
    <div data-testid="camping-accessories-page">
      <SEO
        path="/camping-accessories"
        title="Unique Camping Gear NZ · Bushtech, Tentco & EEZI-AWN"
        description="Ammo box storage, canopy pouches, ensuite floors, wheel cargo mounts and more — the unique overlanding accessories the big chains don't stock."
        image={ACCESSORIES_HERO}
      />

      <section className="relative min-h-[80svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={ACCESSORIES_HERO} alt="Unique camping gear" className="w-full h-full object-cover" style={{ objectPosition: "50% 70%" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-12% via-black/20 via-45% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-20 pt-32 w-full">
          <p className="overline mb-6">— Unique Camping Gear</p>
          <h1 className="font-display text-[11vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.88] text-white hero-text-shadow max-w-[18ch]">
            GEAR THAT EARNS<br />
            <span className="text-[var(--brand-primary)]">ITS SPACE AT CAMP.</span>
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-end">
            <p className="text-base md:text-lg font-medium md:font-normal text-white/95 md:text-white/85 leading-relaxed max-w-xl [text-shadow:0_1px_3px_rgba(0,0,0,0.9),0_2px_20px_rgba(0,0,0,0.7)] md:[text-shadow:none]">
              Proven gear from the same trusted South African brands behind our core range.
            </p>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a href="#range" data-testid="accessories-cta-range" className="btn-primary">View Range <ArrowUpRight size={14} /></a>
              <a href="#enquire" className="btn-secondary">Enquire</a>
            </div>
          </div>
        </div>
      </section>

      <section id="range" className="container-x pt-14 md:pt-20 pb-16 md:pb-20 scroll-mt-24">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline mb-3">— The Range</p>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
              CAMP <span className="text-[var(--brand-primary)]">ESSENTIALS.</span>
            </h2>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-secondary)]">Prices GST inc.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((m, i) => <Card key={m.slug} m={m} testid={`accessory-item-${i}`} />)}
          {PROMOTED_PRODUCTS.map((m, i) => <Card key={m.to} m={m} testid={`accessory-promoted-${i}`} />)}
        </div>
      </section>
    </div>
  );
}
